/** @jsxImportSource ./simboluri-jsx */
import { useState } from "react";

import { Pas } from "./Explica";
import Incarcari3D, { Mod3D } from "./Incarcari3D";
import { FemModel, IncarcariAplicare, IncarcariInfrastructura, IncarcariNivel, IncarcariValoareUnitara, MasaDetaliuNivel } from "./tipuri";
import { t, useT, tx } from "../i18n";
import type { Cheie } from "../i18n";

// Pasul 8 — „Coborârea încărcărilor": elevația clădirii la scara reală de
// înălțime, cu o bară stivuită per planșeu, descompusă pe surse și cumulată
// spre fundație. Lângă ea, aceeași descompunere pe UN metru pătrat de planșeu:
// acolo se citesc direct cifrele din normativ (2,5 / 1,5 / 1,6 kN/m²), care
// altfel stau îngropate în proza coloanei „descriere" a catalogului.
//
// A treia diagramă pune față în față cele două contabilități ale aceleiași
// structuri: încărcarea gravitațională (Pasul 8) și masa seismică (Pasul 7).
// Ele NU sunt aceeași cifră împărțită la g — utila intră cu ψ_E, stâlpii și
// pereții se împart pe jumătate între planșee, iar infrastructura apare doar
// într-una din ele.

// `nume` e cheia din catalog, nu textul: se traduce la randare (`t(s.nume)`).
const SURSE: { key: string; nume: Cheie; culoare: string }[] = [
  { key: "placa", nume: "struct.cob.placa_beton", culoare: "#8ba4b8" },
  { key: "grinzi", nume: "struct.fem.lay.grinzi", culoare: "#5b8fa8" },
  { key: "stalpi", nume: "struct.cob.stalpi", culoare: "#2b5f8a" },
  { key: "pereti_grinzi", nume: "struct.cob.pereti_grinzi", culoare: "#3f9e98" },
  { key: "pereti_placa", nume: "struct.cob.pereti_placa", culoare: "#8fcfca" },
  { key: "finisaje", nume: "struct.cob.finisaje_sdl", culoare: "#8ba888" },
  { key: "utila", nume: "struct.cob.utila_a", culoare: "#d6a5a5" },
  { key: "utila_c", nume: "struct.cob.utila_c", culoare: "#b97f7f" },
  { key: "zapada", nume: "struct.cob.zapada", culoare: "#9fc2d8" },
];

// perechea sursă ↔ cheia din `mase_detaliu` (Pasul 7)
const MASA_KEY: Record<string, keyof MasaDetaliuNivel> = {
  placa: "placa_t",
  grinzi: "grinzi_t",
  stalpi: "stalpi_t",
  pereti_grinzi: "pereti_grinzi_t",
  pereti_placa: "pereti_placa_t",
  finisaje: "finisaje_t",
  utila: "utila_t",
  zapada: "zapada_t",
};

const G = 9.80665;

function nr(v: number | null | undefined, d = 1) {
  return v == null ? "—" : v.toLocaleString("ro-RO", {
    minimumFractionDigits: d, maximumFractionDigits: d,
  });
}

function Legenda({ chei }: { chei: string[] }) {
  return (
    <div className="ci-legend">
      {SURSE.filter((s) => chei.includes(s.key)).map((s) => (
        <span key={s.key} className="ci-legend-item">
          <span className="ci-swatch" style={{ background: s.culoare }} />
          {t(s.nume)}
        </span>
      ))}
    </div>
  );
}

export default function CoborareaIncarcarilor({
  niveluri,
  unitare,
  infra,
  mase,
  model,
  aplicare,
}: {
  niveluri: IncarcariNivel[];
  unitare: IncarcareUnitareProp;
  infra: IncarcariInfrastructura | null;
  mase: MasaDetaliuNivel[];
  model?: FemModel | null;
  aplicare?: IncarcariAplicare[];
}) {
  useT();
  const [tab, setTab] = useState<"kn" | "m2" | "masa" | "3d">("kn");
  const [mod3d, setMod3d] = useState<Mod3D>({ fel: "incarcare", caz: "G1" });
  if (!niveluri?.length) return null;
  const are3d = !!(model?.noduri?.length && aplicare?.length);

  const cheiFolosite = SURSE.map((s) => s.key).filter((k) =>
    niveluri.some((n) => (n.kn[k] ?? 0) > 0.05)
  );

  // ordinea de desen: parterul jos, acoperișul sus
  const ord = [...niveluri].sort((a, b) => a.nivel - b.nivel);
  const totalMax = Math.max(...ord.map((n) => n.total_kn), 1);
  const cumulat: number[] = [];
  let acc = 0;
  for (let i = ord.length - 1; i >= 0; i--) {
    acc += ord[i].total_kn;
    cumulat[i] = acc;
  }
  const laTeren = acc + (infra?.total_kn ?? 0);

  return (
    <div className="st-findings">
      <h3>{t("struct.cob.coborarea_incarcarilor")}</h3>
      <div className="md-tabs">
        <button className={`md-tab ${tab === "kn" ? "active" : ""}`}
                onClick={() => setTab("kn")}>
          {t("struct.cob.niveluri_kn")}
        </button>
        <button className={`md-tab ${tab === "m2" ? "active" : ""}`}
                onClick={() => setTab("m2")}>
          {t("struct.cob.1_m2_planseu")}
        </button>
        <button className={`md-tab ${tab === "masa" ? "active" : ""}`}
                onClick={() => setTab("masa")}>
          {t("struct.cob.incarcare_vs_masa")}
        </button>
        {are3d && (
          <button className={`md-tab ${tab === "3d" ? "active" : ""}`}
                  onClick={() => setTab("3d")}>
            {t("struct.cob.3d_ajung_opensees")}
          </button>
        )}
      </div>

      {tab !== "3d" && <Legenda chei={cheiFolosite} />}

      {tab === "3d" && are3d && (
        <Vedere3D model={model!} aplicare={aplicare!} mod={mod3d}
                  setMod={setMod3d} infra={infra} />
      )}

      {tab === "kn" && (
        <>
          <p className="sg-muted">
            {t("struct.cob.bara_totalul_apasa")}
          </p>
          <table className="sg-table ci-table">
            <thead>
              <tr>
                <th>{t("struct.top.nivel")}</th>
                <th style={{ width: "45%" }}>{t("struct.cob.descompunerea_surse")}</th>
                <th>{t("struct.ret.total")}</th>
                <th>kN/m²</th>
                <th>{t("struct.cob.cumulat_spre_fundatie")}</th>
              </tr>
            </thead>
            <tbody>
              {ord.slice().reverse().map((n, k) => {
                const i = ord.length - 1 - k;
                return (
                  <tr key={n.nivel}>
                    <td>
                      <strong>{n.nume ?? t("struct.cob.nivel_n", { n: n.nivel })}</strong>
                      <div className="sg-muted">
                        {n.z_m != null ? `z = ${nr(n.z_m, 2)} m · ` : ""}
                        {nr(n.arie_m2, 0)} m²
                      </div>
                    </td>
                    <td>
                      <div className="ci-bar"
                           style={{ width: `${(100 * n.total_kn) / totalMax}%` }}>
                        {cheiFolosite.map((key) => {
                          const v = n.kn[key] ?? 0;
                          if (v <= 0.05) return null;
                          const s = SURSE.find((x) => x.key === key)!;
                          return (
                            <span
                              key={key}
                              className="ci-seg"
                              style={{
                                background: s.culoare,
                                flexGrow: v,
                              }}
                              title={`${t(s.nume)}: ${nr(v)} kN `
                                + `(${nr(n.kn_m2[key] ?? 0, 2)} kN/m²)`}
                            />
                          );
                        })}
                      </div>
                    </td>
                    <td className="sg-mono">{nr(n.total_kn, 0)} kN</td>
                    <td className="sg-mono">{nr(n.total_kn_m2, 2)}</td>
                    <td className="sg-mono">{nr(cumulat[i], 0)} kN</td>
                  </tr>
                );
              })}
              {infra && (
                <tr className="ci-infra">
                  <td>
                    <strong>{t("struct.cob.infrastructura")}</strong>
                    <div className="sg-muted">
                      {infra.detaliu_descriere ?? ""}
                    </div>
                  </td>
                  <td className="sg-muted">
                    {t("struct.cob.infra_componente")}
                    {infra.pereti_parter?.total_kn
                      ? t("struct.cob.peretii_parterului_p1", { p1: nr(infra.pereti_parter.total_kn, 0) })
                      : ""}
                  </td>
                  <td className="sg-mono">{nr(infra.total_kn, 0)} kN</td>
                  <td className="sg-mono">—</td>
                  <td className="sg-mono"><strong>{nr(laTeren, 0)} kN</strong></td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="sg-muted">
            {tx("struct.cob.ultima_cifra", { kn: nr(laTeren, 0) })}
          </div>
        </>
      )}

      {tab === "m2" && (
        <>
          <p className="sg-muted">
            {t("struct.cob.aici_verifica_valorile")}
          </p>
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.dr.th_sursa")}</th><th>{t("struct.fem.vf_col_valoarea")}</th><th>{t("struct.ver.formula")}</th><th>{t("struct.cob.vine")}</th>
              </tr>
            </thead>
            <tbody>
              {unitare.map((u) => (
                <tr key={u.sursa}>
                  <td>
                    <span className="ci-swatch" style={{
                      background: SURSE.find((s) => s.key === u.sursa)?.culoare
                        ?? "#ccc",
                    }} />
                    {" "}{u.eticheta}
                  </td>
                  <td className="sg-mono">
                    {u.valoare_kn_m2 == null ? "—"
                      : `${nr(u.valoare_kn_m2, 2)} kN/m²`}
                  </td>
                  <td className="sg-mono">{u.formula}</td>
                  <td className="sg-muted">{u.referinta}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <h4>{t("struct.cob.per_nivel_metru")}</h4>
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.top.nivel")}</th>
                {cheiFolosite.map((k) => (
                  <th key={k}>{t(SURSE.find((s) => s.key === k)!.nume)}</th>
                ))}
                <th>{t("struct.ret.total")}</th>
              </tr>
            </thead>
            <tbody>
              {ord.slice().reverse().map((n) => (
                <tr key={n.nivel}>
                  <td><strong>{n.nume ?? n.nivel}</strong></td>
                  {cheiFolosite.map((k) => (
                    <td key={k} className="sg-mono">
                      {(n.kn_m2[k] ?? 0) > 0.004 ? nr(n.kn_m2[k], 2) : "—"}
                    </td>
                  ))}
                  <td className="sg-mono"><strong>{nr(n.total_kn_m2, 2)}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {tab === "masa" && (
        <>
          <Pas titlu={t("struct.cob.masa_seismica_greutatea")}>
            <p>
              {t("struct.cob.doua_contabilitati_acele")}
            </p>
            <ul>
              <li>{tx("struct.cob.b_utila_b")}</li>
              <li>
                {tx("struct.cob.stalpii_peretii_jumatate")}
              </li>
              <li>{tx("struct.cob.b_infrastructura_b")}</li>
            </ul>
          </Pas>
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.top.nivel")}</th><th>{t("struct.dr.th_sursa")}</th>
                <th>{t("struct.cob.incarcare_pasul_8")}</th>
                <th>{t("struct.cob.masa_seismica_pasul")}</th>
                <th>{t("struct.cob.raport")}</th>
              </tr>
            </thead>
            <tbody>
              {ord.slice().reverse().flatMap((n) => {
                const m = mase.find((x) => x.nivel_diafragma === n.nivel);
                return cheiFolosite.map((k, j) => {
                  const kn = n.kn[k] ?? 0;
                  const mk = MASA_KEY[k];
                  const tone = m && mk ? Number(m[mk] ?? 0) : 0;
                  const knm = tone * G;
                  return (
                    <tr key={`${n.nivel}-${k}`}>
                      {j === 0 ? (
                        <td rowSpan={cheiFolosite.length}>
                          <strong>{n.nume ?? n.nivel}</strong>
                        </td>
                      ) : null}
                      <td>
                        <span className="ci-swatch" style={{
                          background: SURSE.find((s) => s.key === k)!.culoare,
                        }} />{" "}
                        {t(SURSE.find((s) => s.key === k)!.nume)}
                      </td>
                      <td className="sg-mono">{nr(kn, 0)} kN</td>
                      <td className="sg-mono">
                        {tone > 0 ? `${nr(tone, 1)} t = ${nr(knm, 0)} kN` : "—"}
                      </td>
                      <td className="sg-mono">
                        {kn > 0.5 && knm > 0.5 ? nr(knm / kn, 2) : "—"}
                      </td>
                    </tr>
                  );
                });
              })}
            </tbody>
          </table>
          {infra?.total_kn ? (
            <div className="sg-muted">
              {t("struct.cob.infra_nu_masa", { kn: nr(infra.total_kn, 0) })}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

type IncarcareUnitareProp = IncarcariValoareUnitara[];


// Cele trei mecanisme prin care încărcarea și masa ajung efectiv în OpenSees,
// pe modelul real. Nu e o ilustrație: presiunile, kN/ml-ii și masele vin din
// aceleași dicționare cu care se apelează `ops.load` / `ops.eleLoad` /
// `ops.mass`, deci ce se vede e ce primește solverul.
function Vedere3D({
  model, aplicare, mod, setMod, infra,
}: {
  model: FemModel;
  aplicare: IncarcariAplicare[];
  mod: Mod3D;
  setMod: (m: Mod3D) => void;
  infra: IncarcariInfrastructura | null;
}) {
  useT();
  const ap = mod.fel === "incarcare"
    ? aplicare.find((a) => a.caz === mod.caz) : undefined;
  const linii = Object.values(ap?.linii_kn_m ?? {}).map(Number);
  const presiuni = Object.entries(ap?.placa?.pe_nivel ?? {})
    .filter(([, v]) => Number(v) > 0);
  const mase = model.mase ?? [];
  const maseInfra = model.masa_infrastructura ?? [];
  const mTot = mase.reduce((s, [, v]) => s + v, 0);
  const mInfra = maseInfra.reduce((s, [, v]) => s + v, 0);

  return (
    <>
      <div className="ci-3d-ctrl">
        <button className={`md-tab ${mod.fel === "masa" ? "active" : ""}`}
                onClick={() => setMod({ fel: "masa" })}>
          {t("struct.cob.masele_ops")}
        </button>
        {aplicare.map((a) => (
          <button
            key={a.caz}
            className={`md-tab ${mod.fel === "incarcare" && mod.caz === a.caz
              ? "active" : ""}`}
            onClick={() => setMod({ fel: "incarcare", caz: a.caz })}
          >
            {a.caz}
          </button>
        ))}
      </div>

      <Incarcari3D model={model} aplicare={aplicare} mod={mod}
                   mase={mase} masaInfra={maseInfra} />

      {mod.fel === "incarcare" ? (
        <Pas titlu={t("struct.cob.cazul_p1_primeste", { p1: mod.caz })}>
          <ul>
            <li>
              <strong>{t("struct.cob.placa")}</strong>:{" "}
              {presiuni.length
                ? <>{t("struct.cob.presiune_de", {
                      lista: presiuni.map(([n, v]) => t("struct.cob.presiune_pe_nivel",
                        { p: nr(Number(v), 3), n })).join(", "),
                    })}
                    {ap?.placa?.cat
                      ? t("struct.cob.numai_zonele_p1", { p1: ap.placa.cat })
                      : ""}{tx("struct.cob.placa_forte_nodale")}</>
                : t("struct.cob.placa_nimic")}
            </li>
            <li>
              <strong>{t("struct.cob.barele")}</strong>:{" "}
              {linii.length
                ? <>{tx("struct.cob.bare_beam_uniform", {
                      n: linii.length,
                      min: nr(Math.min(...linii), 2),
                      max: nr(Math.max(...linii), 2),
                    })}</>
                : t("struct.cob.bare_nimic")}
            </li>
            {mod.caz === "G4" || mod.caz === "G2c" ? (
              <li>
                {tx("struct.cob.se_aplica_grinzi_fundare")}
                {infra?.detaliu_descriere
                  ? t("struct.cob.sectiunea_p1", { p1: infra.detaliu_descriere }) : ""}.
              </li>
            ) : null}
          </ul>
        </Pas>
      ) : (
        <Pas titlu={t("struct.cob.masele_ops_mass")}>
          <p>{tx("struct.cob.masa_aplica_elemente")}</p>
          <ul>
            <li>
              {tx("struct.cob.bile_pline", { t: nr(mTot, 1) })}
            </li>
            <li>
              <strong>{t("struct.cob.bilele_goale_gri")}</strong> —{" "}
              {tx("struct.cob.bile_goale_text", { t: nr(mInfra, 1) })}
            </li>
            <li>
              {t("struct.cob.deja_impachetat_bile")}
            </li>
          </ul>
        </Pas>
      )}
    </>
  );
}
